// src/components/AddressBankDetails.tsx
import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import FormBuilder, { FieldConfig } from "../FormBuilder";
import Button from "../Button";
import {
  addAddressBank,
  updateAddressBank,
  deleteAddressBank,
} from "../../services/profileService";
import { Icon } from "@iconify/react";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";

export type AddressBank = {
  id?: number | string; // id from backend (address-bank id)
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  district?: string;
  pincode?: string;
  bank_name?: string;
  branch_name?: string;
  account_number?: string;
  ifsc_code?: string;
  account_holder_name?: string;
  // ... any other fields your backend returns
};

type Props = {
  profileId: number | string;
  addressBank?: AddressBank[] | null;
  onChange?: (newList: AddressBank[]) => void; // notify parent
};

export default function AddressBankDetails({
  profileId,
  addressBank = [],
  onChange,
}: Props) {
  const [list, setList] = useState<AddressBank[]>(addressBank ?? []);
  // const [loading, setLoading] = useState(false);

  // modal state for add/edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AddressBank | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(
    null
  );

  // delete-confirm modal state
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<AddressBank | null>(null);
  const [deleting, setDeleting] = useState(false);

     const { user } = useAuth();
    const isAdmin = user?.role === "Admin";
    const isCustomer = user?.role === "Customer";

  useEffect(() => {
    setList(addressBank ?? []);
    // console.log(addressBank);
    // console.log(profileId);
  }, [addressBank]);

  // fields for FormBuilder - adjust types/labels as you like
  const formFields: FieldConfig[] = [
    { name: "address_line1", label: "Address line 1", type: "text",placeholder:"Enter Address Line 1" , required: true },
    { name: "address_line2", label: "Address line 2", type: "text" ,placeholder:"Enter Address Line 2" , required: false },
    { name: "city", label: "City", type: "text",placeholder:"Enter City" , required: true },
    { name: "state", label: "State", type: "text",placeholder:"Enter State" , required: true },
    { name: "district", label: "District", type: "text",placeholder:"Enter District", required: true },
    { name: "pincode", label: "Pincode", type: "text",placeholder:"Enter Pincode" , required: true },

    { name: "bank_name", label: "Bank Name", type: "text" ,placeholder:"Enter Bank Name" },
    { name: "branch_name", label: "Branch Name", type: "text" ,placeholder:"Enter Branch Name"  },
    { name: "account_number", label: "Account Number", type: "text" ,placeholder:"Enter Account Number" },
    { name: "ifsc_code", label: "IFSC Code", type: "text" ,placeholder:"Enter IFSC Code" },
    { name: "account_holder_name", label: "Account Holder", type: "text" ,placeholder:"Enter Account Holder"},
  ];

 function LabelValue({ label, value }: { label: string; value?: any }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start">
      <span className="font-medium text-gray-700 sm:w-32 md:w-36 lg:w-40 flex-shrink-0">
        {label}:
      </span>
      <span
        className="
          text-gray-800 
          flex-1 
          break-words 
          whitespace-pre-wrap 
          overflow-hidden 
          break-all
        "
      >
        {value || "—"}
      </span>
    </div>
  );
}


  const openAdd = () => {
    setEditing(null);
    setFormErrors(null);
    setModalOpen(true);
  };

  const openEdit = (item: AddressBank) => {
    setEditing(item);
    setFormErrors(null);
    setModalOpen(true);
  };

  const handleSubmit = async (data: any) => {
    setSubmitting(true);
    setFormErrors(null);

    try {
      // If files are present your FormBuilder may already return FormData
      const payload =
        data instanceof FormData
          ? data
          : (() => {
            const fd = new FormData();
            Object.entries(data).forEach(([k, v]: any) => {
              if (v === undefined || v === null) return;
              if (v instanceof FileList && v.length) fd.append(k, v[0]);
              else if (v instanceof File) fd.append(k, v);
              else if (typeof v === "object") fd.append(k, JSON.stringify(v));
              else fd.append(k, String(v));
            });
            return fd;
          })();

      // add new
      if (!editing) {
        const res = await addAddressBank(profileId, payload);
        // assume res contains the created entry or id
        const created: AddressBank = res?.address_bank ?? res?.data ?? res;
        const newList = [created, ...list];
        setList(newList);
        onChange?.(newList);
        toast.success(res?.message || "Address & bank details added.");
      } else {
        // update
        const addressId = (editing as any).id ?? (editing as any).address_id;
        const res = await updateAddressBank(profileId, addressId, payload);
        // if res returns updated record use it, else merge
        const updated = res?.address_bank ?? res?.data ?? { ...editing, ...data };
        const newList = list.map((it) =>
          (it as any).id === (updated as any).id ? updated : it
        );
        setList(newList);
        onChange?.(newList);
        toast.success(res?.message || "Address & bank updated.");
      }

      setModalOpen(false);
    } catch (err: any) {
      // standard error handling - adapt depending on axios shape
      const errorData = err?.response?.data;
      setFormErrors(errorData?.errors ?? null);
      toast.error(errorData?.message || "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  // open confirm modal (instead of immediate delete)
  const handleOpenDeleteModal = (item: AddressBank) => {
    setSelectedForDelete(item);
    setConfirmDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedForDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) return;
    const addressId = (selectedForDelete as any).id ?? (selectedForDelete as any).address_id;
    if (!addressId) {
      toast.error("Invalid address id");
      return;
    }

    setDeleting(true);
    try {
      const res = await deleteAddressBank(addressId);
      const newList = list.filter((it) => (it as any).id !== addressId);
      setList(newList);
      onChange?.(newList);
      toast.success(res?.message || "Deleted successfully.");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to delete.");
    } finally {
      setDeleting(false);
      setConfirmDeleteModalOpen(false);
      setSelectedForDelete(null);
    }
  };

  return (
    <div>
      <div className="flex flex-row justify-between items-center mb-5 gap-3">
        <h3 className="text-lg md:text-xl font-semibold text-gray-800">
          Addresses & Bank Details
        </h3>
        { !isCustomer &&(<Button
          onClick={openAdd}
          className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm md:text-base"
        >
          <Icon icon="mdi:plus" width={18} />
          Add
        </Button>)}
      </div>

      <div className="space-y-5">
        {list.length >0?(
        list.map((item, idx) => (
          <div
            key={(item as any).id ?? idx}
            className="bg-white p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg"
          >
            {/* Header */}
            <div className="flex  flex-row items-center justify-between pb-3 mb-2 gap-3 border-b border-gray-200">
              <h4 className="font-semibold text-base md:text-lg text-gray-800 ">
                {list.length > 1 ? `Address ${idx + 1}` : "Address & Bank Details"}
              </h4>

              <div className="flex flex-wrap items-center gap-2">
              { !isCustomer &&(  <button
                  onClick={() => openEdit(item)}
                  className="text-blue-600 border border-blue-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-50 text-sm md:text-base transition"
                >
                  <Icon icon="mingcute:edit-line" className="text-md md:text-lg" />
                  {/* <span className="hidden sm:inline">Edit</span> */}
                </button>)}

                { isAdmin &&(<button
                  onClick={() => handleOpenDeleteModal(item)}
                  className="text-rose-600 border border-rose-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-rose-50 text-sm md:text-base transition"
                >
                  <Icon icon="fluent:delete-24-regular" className="text-md md:text-lg" />
                  {/* <span className="hidden sm:inline">Delete</span> */}
                </button>)}
              </div>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1  lg:grid-cols-2 gap-8">
              {/* Address Section */}
              <div className="grid  span-cols-2 gap-x-6 gap-y-4">
                <LabelValue label="Address line 1" value={item.address_line1} />
                <LabelValue label="Address line 2" value={item.address_line2} />
                <LabelValue label="City" value={item.city} />
                <LabelValue label="District" value={item.district} />
                <LabelValue label="State" value={item.state} />
                <LabelValue label="Pincode" value={item.pincode} />
              </div>

              {/* Bank Section */}
              <div className="grid span-cols-2 gap-x-6 gap-y-4 mt-4 lg:mt-0">
                <LabelValue label="Bank Name" value={item.bank_name} />
                <LabelValue label="Branch Name" value={item.branch_name} />
                <LabelValue label="Account Number" value={item.account_number} />
                <LabelValue label="IFSC" value={item.ifsc_code} />
                <LabelValue label="Account Holder" value={item.account_holder_name} />
              </div>
            </div>
          </div>
        ))
      
      ):(

       <div className="text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm">
      No address available. Please add an address..
    </div>
    )}

</div>




      {/* Add/Edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit Address & Bank" : "Add Address & Bank"}
      >
        <FormBuilder
          defaultValues={editing ?? {}}
          fields={formFields}
          onSubmit={handleSubmit}
          onCancel={() => setModalOpen(false)}
          submitLabel={submitting ? "Saving..." : editing ? "Update" : "Add"}
          error={formErrors}
        />
      </Modal>

      {/* Delete confirmation modal (user provided style) */}
      <Modal
        isOpen={confirmDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirm delete"
      >
        <div className="py-2">
          <p className="mb-4">
            Are you sure you want to delete{" "}
            <strong>
              {selectedForDelete?.address_line1 ??
                selectedForDelete?.bank_name ??
                "this address/bank record"}
            </strong>
            ? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 rounded-lg border hover:bg-gray-100"
              disabled={deleting}
            >
              Cancel
            </button>

            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
